import type { RecordedByKind } from '@domain/product'

/**
 * Quem fez isto: eu, um agente de IA, ou outra pessoa.
 *
 * ## Ele saiu de `RegistrarPreco` antes da segunda cópia
 *
 * O seletor nasceu no ponto de preço — *"quando um agente de IA tiver
 * encontrado alguma coisa, ele diga que foi ele que encontrou aquilo"* — e o
 * recado precisa exatamente da mesma pergunta. Duas cópias seriam duas chances
 * de os rótulos divergirem, e a etiqueta de autoria é o tipo de coisa que só se
 * lê muito depois, quando já não dá para saber qual das duas estava certa.
 *
 * ## O nome só aparece quando NÃO é membro
 *
 * Pedir o nome de quem está logado seria pedir o que já se sabe. Ver
 * `recorded-by.ts` — é lá que `labelDe` resolve o rótulo final, e é ele que
 * transforma um nome em branco em "um agente" em vez de string vazia.
 *
 * ## O app NÃO verifica a autoria, e isso é honesto
 *
 * Quem grava é sempre uma pessoa logada; `ai` é o que o registro **declara**
 * sobre a origem, não algo que o sistema comprove. É a mesma leitura do
 * `recordedBy` do ponto de preço, e vale dizer em voz alta: a etiqueta serve
 * para dar confiança diferente à informação, não para autenticar ninguém.
 */
export function EscolherAutor({
  legenda,
  quem,
  nome,
  doUsuario,
  onQuem,
  onNome,
}: {
  legenda: string
  quem: RecordedByKind
  nome: string
  /** O nome de quem está logado, para rotular a opção "eu". */
  doUsuario: string | null
  onQuem: (quem: RecordedByKind) => void
  onNome: (nome: string) => void
}) {
  return (
    <fieldset className="text-sm">
      <legend className="text-xs text-[var(--text-muted)]">{legenda}</legend>
      <div className="flex flex-wrap gap-2">
        {(
          [
            ['user', doUsuario ?? 'Eu'],
            ['ai', 'Um agente de IA'],
            ['other', 'Outra pessoa'],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            role="radio"
            aria-checked={quem === valor}
            aria-label={rotulo}
            onClick={() => {
              onQuem(valor)
            }}
            className={`rounded-full border px-3 py-1 text-xs ${
              quem === valor
                ? 'border-transparent bg-[var(--text)] text-[var(--bg)]'
                : 'border-[var(--border)]'
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {quem !== 'user' && (
        <label className="mt-2 block">
          <span className="block text-xs text-[var(--text-muted)]">
            {quem === 'ai' ? 'Qual agente' : 'Quem'}
          </span>
          <input
            value={nome}
            aria-label="Nome de quem encontrou"
            placeholder={quem === 'ai' ? 'Claude' : 'minha mãe'}
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(e) => {
              onNome(e.target.value)
            }}
          />
        </label>
      )}
    </fieldset>
  )
}
